import clsx from 'clsx';
import { IContaBancaria, IResumoBancario, ISugestaoContaBancaria } from '../../interfaces/IModuloBancario';
import ComprovantesBancarios from './comprovantes-bancarios';
import { EscolherCompetencia } from './etapas-modulo';
import ExtratosBancarios from './extratos-bancarios';
import FolhaAnalitica from './folha-analitica';
import NotasFiscais from './notas-fiscais';
import PagamentosRfb from './pagamentos-rfb';
import RetornosCobranca from './retornos-cobranca';

export type TipoArquivo = 'extratos' | 'comprovantes' | 'cobranca' | 'rfb' | 'nfse' | 'folha';

export const TIPOS_ARQUIVO: TipoArquivo[] = ['extratos', 'comprovantes', 'cobranca', 'rfb', 'nfse', 'folha'];

const ABAS: { id: TipoArquivo; titulo: string; icone: string; explica?: string; contar: (r: IResumoBancario) => number }[] = [
    { id: 'extratos', titulo: 'Extratos', icone: 'fa-university', contar: r => r.extratosTotal },
    { id: 'comprovantes', titulo: 'Comprovantes', icone: 'fa-receipt', explica: 'qual provisão foi paga, quando há mais de uma com o mesmo valor', contar: r => r.comprovantes },
    { id: 'cobranca', titulo: 'Cobrança', icone: 'fa-money-check-alt', explica: 'por que o crédito do banco é diferente do valor do título', contar: r => r.retornos },
    { id: 'rfb', titulo: 'RFB', icone: 'fa-landmark', explica: 'os débitos de tributos pagos à Receita', contar: r => r.pagamentosRfb },
    { id: 'nfse', titulo: 'NFS-e', icone: 'fa-file-invoice', explica: 'por que o banco pagou menos que o bruto da nota', contar: r => r.nfse },
    { id: 'folha', titulo: 'Folha', icone: 'fa-users', explica: 'os débitos de salário, por empregado', contar: r => r.folha },
];

interface Props {
    tipo: TipoArquivo;
    onTipo: (tipo: TipoArquivo) => void;
    esteiraId: number;
    competencia: string;
    competencias: string[];
    onCompetencia: (competencia: string) => void;
    contas: IContaBancaria[];
    resumo: IResumoBancario | null;
    versao: number;
    versaoMinutas: number;
    onAlterou: () => void;
    onCadastrarConta: (sugestao: { arquivoId: number; dados: ISugestaoContaBancaria }) => void;
}

// Todos os arquivos do módulo num lugar só; só a aba aberta consulta o servidor.
export default function ArquivosModulo({ tipo, onTipo, esteiraId, competencia, competencias, onCompetencia, contas, resumo, versao, versaoMinutas, onAlterou, onCadastrarConta }: Props) {
    const aba = ABAS.find(a => a.id === tipo) ?? ABAS[0];

    return (
        <>
            <ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 flex-nowrap mb-4' style={{ overflowX: 'auto', overflowY: 'hidden' }} role='tablist'>
                {ABAS.map(a => {
                    const quantidade = resumo ? a.contar(resumo) : 0;
                    return (
                        <li className='nav-item text-nowrap' key={a.id} role='presentation'>
                            <button type='button' role='tab' aria-selected={a.id === tipo} className={clsx('nav-link btn btn-flush px-3 py-2', a.id === tipo && 'active')} onClick={() => onTipo(a.id)}>
                                <i className={clsx('fas me-2', a.icone)}></i>{a.titulo}
                                {quantidade > 0 && <span className='badge badge-light-primary ms-2'>{quantidade.toLocaleString('pt-BR')}</span>}
                            </button>
                        </li>
                    );
                })}
            </ul>

            {aba.explica && (
                <div className='text-muted fs-7 mb-3 px-1'>
                    <span className='badge badge-light me-2'>Opcional</span>Este documento ajuda a explicar {aba.explica}.
                </div>
            )}

            {tipo === 'extratos' && (
                <ExtratosBancarios esteiraId={esteiraId} competencia={competencia} contas={contas} versao={versao} onAlterou={onAlterou} onCadastrarConta={onCadastrarConta} />
            )}

            {tipo !== 'extratos' && !competencia && (
                <EscolherCompetencia competencias={competencias} onEscolher={onCompetencia} motivo='Os documentos são enviados e conferidos por mês.' />
            )}

            {tipo !== 'extratos' && competencia && (
                <>
                    {tipo === 'comprovantes' && <ComprovantesBancarios esteiraId={esteiraId} competencia={competencia} versaoMinutas={versaoMinutas} onAlterou={onAlterou} />}
                    {tipo === 'cobranca' && <RetornosCobranca esteiraId={esteiraId} competencia={competencia} contas={contas} versaoMinutas={versaoMinutas} onAlterou={onAlterou} />}
                    {tipo === 'rfb' && <PagamentosRfb esteiraId={esteiraId} competencia={competencia} versaoMinutas={versaoMinutas} onAlterou={onAlterou} />}
                    {tipo === 'nfse' && <NotasFiscais esteiraId={esteiraId} competencia={competencia} versaoMinutas={versaoMinutas} onAlterou={onAlterou} />}
                    {tipo === 'folha' && <FolhaAnalitica esteiraId={esteiraId} competencia={competencia} versaoMinutas={versaoMinutas} onAlterou={onAlterou} />}
                </>
            )}
        </>
    );
}
